import { DateTime, Duration, Info, Interval, ToRelativeUnit } from "luxon";
import { capitalCase } from "@portal/app/_common/lib/changeCase";
import { getDateFormatted, hasSame, isSameYear } from "./dateTimeUtils";
import { TimeOfDayBucket } from "../types/cohort";
import { ProgramDocumentType } from "../types/document";
import {
  GEO_RESTRICTION,
  GeoRestriction,
  GeoRestrictionRegion,
  ProgramEnvironment,
} from "../types/program";
import { DOB_FORMAT } from "../types/customerDetail";

/**
 * Clean up an already-valid email address.
 */
export const cleanEmailAddress = <Email extends string | undefined | null>(
  email: Email
): Email extends undefined ? string | undefined : string =>
  email?.trim().toLowerCase() as Email extends undefined ? string | undefined : string;

/**
 * Clean up an already-valid phone number.
 * Strips the leading US country code "1" if the number is 11 digits.
 */
export const cleanPhoneNumber = <Phone extends string | undefined | null>(
  phone: Phone
): Phone extends undefined ? string | undefined : string => {
  if (!phone) return undefined as Phone extends undefined ? string | undefined : string;
  let digits = phone.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    digits = digits.slice(1);
  }
  return cleanMaxLengthNumber(10)(digits) as Phone extends undefined ? string | undefined : string;
};

/**
 * Processes a string by only keeping numeric characters. Returns a string with leading
 * zeroes intact (useful for ZIP codes or other number strings -- for integers, do not
 * use this method.
 *
 *   are sliced off.
 * @param maxLength
 */

export const cleanMaxLengthNumber =
  (maxLength?: number) =>
  (value = ""): string =>
    (value || "").replace(/[^\d]/g, "").slice(0, maxLength);

/**
 * Parses a string by discarding all non-numeric characters and then converting to a number.
 * Empty/non-numeric values will return 0.
 *
 * @param value
 */
export const cleanInteger = (value = ""): number =>
  parseInt(cleanMaxLengthNumber()(value), 10) || 0;

/**
 * Renders an integer into a string, handling missing inputs gracefully. This is a thunk; you can
 * pass whether or not a 0 or empty field should be rendered as 0.
 *
 * The behavior of this function is unclear if you pass in a float.
 */
export const renderInteger =
  (shouldRenderZero = false) =>
  (value?: number): string =>
    !shouldRenderZero && !value ? "" : (value || 0).toLocaleString("en-US");

/**
 * Returns a formatted version of a USA 10-digit phone number. The goal is to always
 * return something, even when incomplete, and to remove unnecessary values. This
 * formatter will ignore everything except for the first 10 digits.
 *
 * @param number
 */
export const renderPhoneNumber = (number?: string): string => {
  if (!number) {
    return "";
  }
  const cleanNumber = number.replace(/[^\d]/g, "");
  const cleanNumberLength = cleanNumber.length;

  if (cleanNumberLength <= 3) {
    return cleanNumber;
  }
  if (cleanNumberLength <= 6) {
    return `${cleanNumber.slice(0, 3)}-${cleanNumber.slice(3)}`;
  }

  return `(${cleanNumber.slice(0, 3)}) ${cleanNumber.slice(3, 6)}-${cleanNumber.slice(6, 10)}`;
};

export const parsePhoneNumber = (value?: string): string => cleanPhoneNumber(value) ?? "";

export const toDOBDisplay = (dob?: string): string => {
  if (!dob) {
    return "";
  }
  const cleanNumber = dob.replace(/[^\d]/g, "");
  if (dob.length === 10) {
    return `${cleanNumber.slice(4, 6)}/${cleanNumber.slice(6, 8)}/${cleanNumber.slice(0, 4)}`;
  }
  const cleanNumberLength = cleanNumber.length;

  if (cleanNumberLength <= 2) {
    return cleanNumber;
  }
  if (cleanNumberLength <= 4) {
    return `${cleanNumber.slice(0, 2)}/${cleanNumber.slice(2)}`;
  }

  return `${cleanNumber.slice(0, 2)}/${cleanNumber.slice(2, 4)}/${cleanNumber.slice(4, 8)}`;
};

export const fromDOBDisplay = (value?: string): string => {
  const date = DateTime.fromFormat(value ?? "", "MM/dd/yyyy");
  return date.isValid ? date.toFormat(DOB_FORMAT) : cleanMaxLengthNumber(8)(value);
};

/**
 * For a person's name. Only keeps letters, dashes, apostrophes, and spaces.
 */
const cleanName = (value = ""): string => (value || "").replace(/[^a-zA-Z-\\' ]/g, "");

/**
 * For a person's first name. Same as `cleanName`, but also capitalizes the first
 * letter. Note: Use this for aesthetic reasons conservatively. For example, some
 * last names do not start with capital letters.
 */
export const cleanAndCapitalizeName = (value = ""): string => {
  const name = cleanName(value);
  return name.charAt(0).toUpperCase() + name.slice(1);
};

/**
 * Renders a User's full name.
 */
export const renderFullName = (
  user: {
    nameFirst?: string | null;
    nameLast?: string | null;
  } | null
) => [(user?.nameFirst || "").trim(), (user?.nameLast || "").trim()].filter(Boolean).join(" ");

/**
 * Renders an integer, representing cents, as a dollar amount.
 */
export const renderDollarAmount = (
  cents: number | null | undefined = 0,
  shouldRenderZero = false,
  shouldHideCents = false
): string => {
  // set to 0 if shouldHideCents = true AND dollar amount has no cents
  const fractionDigits = shouldHideCents && cents && cents % 100 === 0 ? 0 : 2;

  return cents
    ? Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }).format(cents / 100)
    : shouldRenderZero && shouldHideCents
      ? "$0"
      : shouldRenderZero
        ? "$0.00"
        : "";
};

/**
 * Parses a MM-DD-YYYY (with non-number characters stripped) into a DateTime.
 * If invalid, returns null.
 */
export const parseDate = (value = ""): DateTime | null => {
  value = cleanMaxLengthNumber(8)(value);
  if (value.length !== 8) {
    return null;
  }

  const dt = DateTime.fromFormat(value, "LLddyyyy");
  if (!dt.isValid) {
    return null;
  }

  return dt;
};

/**
 * Renders a DateTime as "MM/DD/YYYY".
 */
export const renderDate = (dt: DateTime | null): string =>
  dt && dt.isValid ? dt.toFormat("LL/dd/yyyy") : "";

/**
 * Formats free-form user input for dates that will eventually be fed to `parseDate`.
 */
export const formatDate = (input?: string): string => {
  if (!input) {
    return "";
  }
  const cleanNumber = input.replace(/[^\d]/g, "");
  const cleanNumberLength = cleanNumber.length;

  if (cleanNumberLength <= 2) {
    return cleanNumber;
  }
  if (cleanNumberLength <= 4) {
    return `${cleanNumber.slice(0, 2)}/${cleanNumber.slice(2)}`;
  }

  return `${cleanNumber.slice(0, 2)}/${cleanNumber.slice(2, 4)}/${cleanNumber.slice(4, 8)}`;
};

/**
 * Formats free-form user input for times that will eventually be fed to `parseTime`.
 *
 * Since the user input is very flexible, accepting everything from "noon" to "330p", we need to
 * employ a light touch on formatting. We do only the following:
 * - If there are numbers, we insert a colon `:` before the last 2 digits, if they exist
 * - If there are numbers and letters, we strip all letters that aren't the meridian (`a`, `p`, or `m`).
 */
export const formatTime = (input = ""): string => {
  const numbersOnly = cleanMaxLengthNumber()(input);
  let formattedTime = numbersOnly;
  if (numbersOnly.length) {
    const amPM =
      input
        .match(/[APM]+/gi)?.[0]
        .toUpperCase()
        .padEnd(2, "M") || "";

    if (numbersOnly.length > 2) {
      const minutes = numbersOnly.match(/(\d{2})(?!.*\d)/i)?.[0] || "";
      const hours = numbersOnly.replace(minutes, "");
      formattedTime = `${hours}:${minutes}`;
    }

    if (amPM) {
      formattedTime = `${formattedTime} ${amPM}`;
    }
  }

  return formattedTime;
};

export const isTimeValid = (input = ""): boolean =>
  input ? DateTime.fromFormat(input, "HH:mm").isValid : false;

/**
 * Returns a human-readable string representing days of the week. Be aware that
 * in the worst-case scenario this string can be very long. If no days are provided,
 * or invalid days, then "No days of the week" is returned.
 *
 * @param daysOfWeek string with ordered numbers 1-7 that represent days of a week
 *   (e.g. "12345" represents weekdays)
 * @param isCapitalized if True (default), the first letter is capitalized;
 *   pass False here to use in a sentence
 */
export const getPrettyDaysOfWeek = (
  daysOfWeek: string[] | string | null | undefined = "",
  isCapitalized = true
): string => {
  if (!daysOfWeek || daysOfWeek.length === 0) {
    return isCapitalized ? "No days of the week" : "no days of the week";
  }

  const daysOfWeekArray = typeof daysOfWeek !== "string" ? daysOfWeek : daysOfWeek.split("");

  const daysOfWeekNumbers = daysOfWeekArray.map((dayOfWeek) => Number(dayOfWeek) - 1);
  const isInSuccession = daysOfWeekNumbers.every(
    (daysOfWeekNumber, i) => i === 0 || daysOfWeekNumber === daysOfWeekNumbers[i - 1] + 1
  );
  const luxonDaysOfWeek = daysOfWeekNumbers.map(
    (luxonDayOfWeek) => Info.weekdays("short", { locale: "en" })[luxonDayOfWeek]
  );
  /**
   * eg. Mon
   */
  if (luxonDaysOfWeek.length === 1) {
    return luxonDaysOfWeek[0];
  }
  /**
   * eg. Mon & Tue
   */
  if (luxonDaysOfWeek.length === 2) {
    if (daysOfWeek === "67") {
      return isCapitalized ? "Weekends (Sat & Sun)" : "weekends (Sat & Sun)";
    }
    return luxonDaysOfWeek[0] + " & " + luxonDaysOfWeek[1];
  }
  if (isInSuccession) {
    /**
     * eg. Mon-Wed
     */
    if (daysOfWeek === "12345") {
      return isCapitalized ? "Weekdays (Mon-Fri)" : "weekdays (Mon-Fri)";
    }
    if (daysOfWeek === "1234567") {
      return isCapitalized ? "Every day (Mon-Sun)" : "every day (Mon-Sun)";
    }
    return `${luxonDaysOfWeek[0]}-${luxonDaysOfWeek[luxonDaysOfWeek.length - 1]}`;
  } else {
    /**
     * eg. Mon, Tue, & Thu
     */
    const firstLuxonDaysOfWeek = luxonDaysOfWeek.slice(0, luxonDaysOfWeek.length - 1);
    const lastDay = luxonDaysOfWeek[luxonDaysOfWeek.length - 1];
    return `${firstLuxonDaysOfWeek.join(", ")}, & ${lastDay}`;
  }
};

export const getPrettyTimeRangeInterval = ({
  timeStart = "",
  timeEnd = "",
}: {
  timeStart: string;
  timeEnd: string;
}) => {
  const dtStart = DateTime.fromISO(timeStart);
  const dtEnd = DateTime.fromISO(timeEnd);

  if (!dtStart.isValid || !dtEnd.isValid) {
    return "";
  }
  let start = "h";
  let end = "h";
  if (dtStart.minute !== 0) {
    start += ":mm";
  }
  if (dtEnd.minute !== 0) {
    end += ":mm";
  }
  if (dtStart.hour === 0 && dtStart.minute === 0) {
    start = "'midnight'";
  }
  if (dtEnd.hour === 0 && dtEnd.minute === 0) {
    end = "'midnight'";
  }
  if (dtStart.hour === 12 && dtStart.minute === 0) {
    start = "'noon'";
  }
  if (dtEnd.hour === 12 && dtEnd.minute === 0) {
    start += " a";
    end = "'noon'";
  }
  if (end !== "'midnight'" && end !== "'noon'") {
    end += " a";
  }
  if (
    dtStart.toFormat("a") !== dtEnd.toFormat("a") &&
    start !== "'midnight'" &&
    start !== "'noon'"
  ) {
    start += " a";
  }

  // Note that this dash is an en-dashes, not a hyphen
  return dtStart.toFormat(start) + "–" + dtEnd.toFormat(end);
};

/**
 * Takes two ISODates and generates a tuple that can be used to render a
 * friendly date range, combining overlapping days, months, and years when
 * possible.
 *
 * If you are rendering this, use <FriendlyDateRange> component which handles
 * non-breaking segments and a nice dash for you.
 *
 * ## Examples
 *
 * - January 1, 2022
 * - January 1 - 31, 2022
 * - January 1 - February 1, 2022
 * - January 1, 2022 - February 1, 2023
 *
 * ## Usage
 *
 * The tuple should be joined with a dash in between, unless the second element
 * is null, in which only the first element should be rendered:
 * - ["Foo", null] => Render as "Foo"
 * - ["Foo", "bar"] => Render as "Foo - bar"
 *
 * If start date (this is allowed for maximum flexibility), then ["", null] is
 * returned. (This is a nonsense response.)
 */
export const getFriendlyDateRangeFromDateStrings = (
  // Start ISODate
  startDate?: string | null,
  // End ISODate
  endDate?: string | null,
  {
    useShortMonthNames = false,
    alwaysIncludeYear = false,
  }: {
    /**
     * If True, short months ("Jan") will be returned.
     * If False (default), full months ("January") will be used.
     */
    useShortMonthNames?: boolean;
    /**
     * If True, the year will be included in one or both of start/end even
     * if it's in the current year.
     * If False (default), the year will be omitted if both dates are in the
     * current year (e.g. 2023 dates will not return "2023" in the response if
     * the current year is 2023).
     */
    alwaysIncludeYear?: boolean;
  } = {}
): [string, string | null] => {
  const m = useShortMonthNames ? "MMM" : "MMMM";

  // Escape hatch if start date is not provided
  if (!startDate) {
    return ["", null];
  }

  // We return only the start date if the end date is not provided, or if the
  // end date is the same as the start date. The format is always either
  // "m d" (if currently in the same year) or "m d, y" (if not).
  const isSameDay = !endDate || hasSame(startDate, endDate, "day");
  if (isSameDay) {
    const format = isSameYear(startDate) && !alwaysIncludeYear ? `${m} d` : `${m} d, y`;
    return [getDateFormatted(startDate, format), null];
  }

  const areBothCurrentYear = isSameYear(startDate) || isSameYear(endDate);
  const areSameYear = hasSame(startDate, endDate, "year");
  const areSameMonth = hasSame(startDate, endDate, "month");

  let startFormat, endFormat;
  if (areSameMonth) {
    startFormat = `${m} d`;
    endFormat = `d${!areBothCurrentYear || alwaysIncludeYear ? ", y" : ""}`;
  } else if (areSameYear && areBothCurrentYear) {
    startFormat = `${m} d${alwaysIncludeYear ? ", y" : ""}`;
    endFormat = `${m} d${alwaysIncludeYear ? ", y" : ""}`;
  } else {
    startFormat = `${m} d, y`;
    endFormat = `${m} d, y`;
  }

  return [getDateFormatted(startDate, startFormat), getDateFormatted(endDate, endFormat)];
};

/**
 * @param properties from CohortDate
 * @returns string describing the date schedule
 * example: Jan 18-27, 2022 (Mon, Tues, & Wed) 10 AM–1 PM, 9 days
 */
export const getDescriptiveScheduleForDate = ({
  name = "",
  timeStart = "",
  timeEnd = "",
  daysOfWeek = "",
  startDate,
  endDate,
}: {
  name: string | null | undefined;
  timeStart: string;
  timeEnd: string;
  daysOfWeek: string | null | undefined;
  startDate: string;
  endDate: string;
}) => {
  let dateDescription = "";
  dateDescription += name ? `${name} - ` : "";
  dateDescription += `${getFriendlyDateRangeFromDateStrings(startDate, endDate, {
    useShortMonthNames: true,
    alwaysIncludeYear: true,
  }).join("-")}: `;
  dateDescription += `${getPrettyDaysOfWeek(daysOfWeek)} `;
  dateDescription += `${getPrettyTimeRangeInterval({ timeStart, timeEnd })} `;
  dateDescription += `(${getFuzzyDateRangeFromDateTimeStrings({
    startDate,
    endDate,
  })})`;

  return dateDescription;
};

const dateRangeScales: [number, ToRelativeUnit][] = [
  [1.8, "years"],
  [1.8, "months"],
  [1.8, "weeks"],
  [1.8, "days"],
  [1.6, "hours"],
  [1.6, "minutes"],
  [0, "seconds"],
];

/**
 * Returns a humanized string like "2 weeks" or "4 months" given ISO DateTime
 * strings. This converts the DateTimes into an Interval, then into a Duration.
 * If a `startDate` is not provided, the current local time is used.
 *
 * @deprecated Instead, convert at call time to Duration, and then use
 * `getFuzzyDateRangeFromDuration`. This provides unambiguous timezone behavior
 * when converting ISODate/Times to Luxon DateTimes.
 */
export const getFuzzyDateRangeFromDateTimeStrings = ({
  startDate,
  endDate,
}: {
  startDate?: string;
  endDate: string;
}) => {
  const duration = Interval.fromDateTimes(
    startDate ? DateTime.fromISO(startDate) : DateTime.local(),
    DateTime.fromISO(endDate)
  ).toDuration();

  return getFuzzyDateRangeFromDuration(duration);
};

/**
 * Returns a humanized string like "2 weeks" or "4 months" given a Luxon
 * Duration. These are intentionally blurry (i.e. "2 weeks", not "13 days") so
 * they should not be used in contexts where accuracy of length is demanded.
 */
export const getFuzzyDateRangeFromDuration = (duration: Duration): string => {
  for (const [max, unit] of dateRangeScales) {
    if (duration.as(unit) >= max) {
      return Math.round(duration.as(unit)) + " " + unit;
    }
  }

  // Fallback
  return Math.round(duration.as("days")) + " days";
};

/**
 * Returns a human-readable string representing 12-hour time with ante/post meridiem
 * @param isoTime string following the 24 hour format HH:mm
 */

export const getPrettyTime = (isoTime: string) => {
  return DateTime.fromFormat(isoTime, "HH:mm").toFormat("hh:mm a");
};

const endsAtOrAfter1pm = (endTime: string, dateFormat: string) => {
  const endDateObj = DateTime.fromFormat(endTime, dateFormat);
  return endDateObj.hour >= 13;
};

/**
 * Returns a time-of-day bucket (like "MORNING", "AFTERNOON", "EVENING", "NIGHT", or "FULL_DAY")
 *
 * Ranges are defined as:
 * - Morning: 6am-noon, ends before 1pm
 * - Full day: starts in the morning and ends at or after 1pm
 * - Afternoon: noon-5pm
 * - Evening: 5-10pm
 * - Night: after 10pm and before 6am
 *
 * @param startTime string: ISO time string (HH:mm)
 * @param endTime string: ISO time string (HH:mm)
 */
export const getTimeOfDayBucket = (
  startTime: string,
  endTime?: string
): TimeOfDayBucket | undefined => {
  if (startTime === "") {
    return undefined;
  }

  // check if startTime is formatted with seconds (has 2 colons)
  const dateFormat = startTime.split(":").length - 1 === 2 ? "HH:mm:ss" : "HH:mm";

  const startDateObj = DateTime.fromFormat(startTime, dateFormat);
  const hour = startDateObj.hour;
  if (hour >= 0 && hour < 12) {
    if (endTime && endsAtOrAfter1pm(endTime, dateFormat)) {
      return "FULL_DAY"; // ends after or at 1pm
    }
    return "MORNING";
  } else if (hour >= 12 && hour < 17) {
    return "AFTERNOON";
  } else if (hour >= 17 && hour < 22) {
    return "EVENING";
  } else {
    return "NIGHT";
  }
};

/**
 * Get a friendly string for intake document types.
 */
export const getFriendlyDocumentTypes = (docType: ProgramDocumentType): string => {
  switch (docType) {
    case "hs_diploma_ged":
      return "High School Diploma/GED";
    case "ss_card":
      return "Social Security Card";
    case "drivers_license":
      return "Driver's License / State ID";
    case "recent_tb_test":
      return "Recent TB Test";
    case "recent_covid_test":
      return "Recent COVID-19 Test";
    case "covid_vaccination":
      return "Proof of COVID-19 Vaccination";
    default:
      return capitalCase(docType);
  }
};

export const getFriendlyEnvironment = (environment: ProgramEnvironment | undefined | null) => {
  if (!environment) {
    return undefined;
  }
  switch (environment) {
    case "IN_PERSON":
      return "In-Person" as const;
    case "HYBRID":
      return "Hybrid" as const;
    case "ONLINE":
      return "Online" as const;
    default:
      throw new Error(`Unhandled environment type: ${environment}`);
  }
};

/**
 * Join elements together with commas and "and"/"&". For example, "A, B, and C".
 * We use the Oxford comma in this part of town!
 *
 * 0 elements -> ""
 * 1 element -> A
 * 2 elements -> A and B
 * 3+ elements -> A, B, and C
 */
export const concatenateStringsWithCommas = (
  elements: string[],
  // Common options include "and", "&", and "or"
  lastElementPrefix = "and"
): string => {
  if (!elements?.length) {
    return "";
  }
  if (elements.length === 1) {
    return elements[0];
  }

  if (elements.length === 2) {
    return `${elements[0]} ${lastElementPrefix} ${elements[1]}`;
  }

  return `${elements
    .slice(0, elements.length - 1)
    .join(", ")} ${lastElementPrefix} ${elements[elements.length - 1]}`;
};

export const getFriendlyGeoRestriction = (
  geoRestriction: GeoRestriction,
  geoRestrictionRegion?: GeoRestrictionRegion | null
) => {
  if (geoRestriction === GEO_RESTRICTION.NONE) {
    return "None";
  }

  if (!geoRestrictionRegion) {
    return "Restricted to only students in certain states";
  }

  return `Restricted to only students in: ${concatenateStringsWithCommas(
    geoRestrictionRegion.state
  )}`;
};

/**
 * Format a name to be displayed in a short format, e.g. in a testimonial.
 */
export function formatShortName(name: string): string {
  // Split the name by one or more spaces and filter out any empty strings
  const nameParts = name.trim().split(/\s+/);
  const firstName = nameParts[0] ?? "";
  const lastName = nameParts[1] ? formatLastInitial(nameParts[1]) : "";
  return `${firstName}${lastName ? ` ${lastName}` : ""}`;
}

export function formatLastInitial(lastName: string): string {
  if (lastName.length === 0) {
    return "";
  }

  // Check if the input is a single letter with a period
  if (/^[A-Z]\.$/i.test(lastName)) {
    return lastName;
  }

  // Check if the input is a single letter without a period
  if (/^[A-Z]$/i.test(lastName)) {
    return `${lastName}.`;
  }

  // Otherwise, assume it's a full last name and return the first letter with a period
  return `${lastName.charAt(0).toUpperCase()}.`;
}
